import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { DEFAULT_PAGE_SIZE } from '@/components/constants/constants';
import type { Options } from '@/components/molecules/SelectForm';
import { TEAM_LEAGUES_MODULE } from '@/entities/matchup/constants';
import { SearchParams } from '@/entities/SearchParams';
import {
	ACTIVE_TEAMS_QUERY_KEY,
	DISTRICTS_INFO_QUERY_KEY,
	FEATURED_LEAGUES_QUERY_KEY,
	MLP_STRIP_QUERY_KEY,
	PARTNERS_OPPONENTS_STATS_QUERY_KEY,
	PLAYER_ACTIVITY_QUERY_KEY,
	PLAYER_GRAPH_STATS_QUERY_KEY,
	PLAYER_OVERALL_STATS_QUERY_KEY,
	SCHEDULED_EVENTS_QUERY_KEY,
	SCORE_FORMATS_QUERY_KEY,
	SEASON_DIVISION_SUBSTITUTE_PLAYERS_QUERY_KEY,
	SYSTEM_USERS_SEARCH_QUERY_KEY,
	TEAM_LEAGUE_MODULE_FOR,
	TEAM_PLAYER_ACTIVITY_QUERY_KEY
} from '@/hooks/constants';
import { playerJoinRequest } from '@/infrastructure/network/action/team-leagues-team-players-actions';
import { getDistricts } from '@/infrastructure/network/data/districts/districts';
import { getFeaturedLeagues, getMLPStripEvents } from '@/infrastructure/network/data/homepage/team-leagues';
import { createInvitationCode, updateInvitationCode } from '@/infrastructure/network/data/invitation-code/invitation-code';
import { getScoreFormats } from '@/infrastructure/network/data/lookups/score-formats';
import { getStates } from '@/infrastructure/network/data/lookups/states';
import { sendMessage } from '@/infrastructure/network/data/messaging/messaging';
import { getSponsors } from '@/infrastructure/network/data/module/sponsors';
import { getPartnersOpponentsStats } from '@/infrastructure/network/data/player/partners-opponents-stats';
import { getPlayerOverallStats } from '@/infrastructure/network/data/player/player-overall-stats';
import { getPlayerGraphStats } from '@/infrastructure/network/data/player/player-stats';
import { setPlayerAsInjured } from '@/infrastructure/network/data/player/set-injured';
import { getSeasonDivisionSubstitutePlayers } from '@/infrastructure/network/data/player/substitute-player';
import { updateIsFreeAgent } from '@/infrastructure/network/data/player/update-is-free-agent';
import { getScheduledEvents } from '@/infrastructure/network/data/scheduled-event/event';
import { listSeasonDivisons } from '@/infrastructure/network/data/season-divisions/season-divisions';
import { listSeasons } from '@/infrastructure/network/data/seasons/seasons';
import { listActiveTeams } from '@/infrastructure/network/data/team-dashboard/active-teams';
import { listPlayerActivity } from '@/infrastructure/network/data/team-dashboard/player-activity';
import { getSystemUsersSearch } from '@/infrastructure/network/data/user/system-user-search';
import { CreateInvitationCodeRequest, UpdateInvitationCodeRequest } from '@/infrastructure/network/dto/requests/invitation-code';
import { SendMessageRequest } from '@/infrastructure/network/dto/requests/messaging';
import { SetPlayerAsInjuredRequest, UpdateIsFreeAgentRequest } from '@/infrastructure/network/dto/requests/player';
import { addLeagueToFavorite, removeLeagueFromFavorite } from '@/services/actions/league/leagueFavorites';
import { useOrganizationStore } from '@/store/organization-store';

interface ApproveOrDeclinePlayerRequest {
	teamLeagueId: string;
	organizationId: string;
	teamId: string;
	tradeRequestUuid: string;
	action: string;
	seasonId: string;
}

export const useStatesQuery = (countryId: number) => {
	return useQuery<Options[]>({
		queryKey: [`states=${countryId}`],
		queryFn: async () => {
			const response = await getStates({ countryId: countryId.toString() });

			if (response.statusCode !== 200) {
				throw new Error(`Failed to fetch states: ${response.statusCode}`);
			}

			return (
				response.data?.map((state) => ({
					value: state.id.toString(),
					label: state.title
				})) || []
			);
		},
		enabled: !!countryId,
		placeholderData: keepPreviousData
	});
};

export const useSeasonsQuery = () => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [`seasons`, store.organizationUuid, store.teamLeagueUuid],
		queryFn: () =>
			listSeasons({
				organizationUuid: store.organizationUuid,
				teamLeagueUuid: store.teamLeagueUuid
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid
	});
};

export const useSeasonDivisions = (organizationUuid: string, teamLeagueUuid: string, selectedSeason: string | null) => {
	return useQuery({
		queryKey: ['seasonDivisions', organizationUuid, teamLeagueUuid, selectedSeason],
		queryFn: async () =>
			listSeasonDivisons({
				organizationUuid,
				teamLeagueUuid,
				seasonUuid: selectedSeason!
			}),
		enabled: !!organizationUuid && !!teamLeagueUuid && !!selectedSeason
	});
};

export const useSponsors = (teamLeagueUuid: string) => {
	return useQuery({
		queryKey: ['sponsors', teamLeagueUuid],
		queryFn: () => getSponsors({ moduleUuid: teamLeagueUuid, moduleFor: TEAM_LEAGUE_MODULE_FOR }),
		enabled: !!teamLeagueUuid
	});
};

interface PlayerActivityArgs {
	selectedSeason: string | null;
	disabled?: boolean;
	searchParams?: ReadonlyURLSearchParams;
}

export const usePlayerActivity = ({ selectedSeason, disabled = false, searchParams }: PlayerActivityArgs) => {
	const store = useOrganizationStore();

	return useQuery({
		queryKey: [PLAYER_ACTIVITY_QUERY_KEY, store.organizationUuid, store.teamLeagueUuid, selectedSeason, searchParams?.get('district')],
		queryFn: () =>
			listPlayerActivity({
				organizationUuid: store.organizationUuid,
				teamLeagueUuid: store.teamLeagueUuid,
				seasonUuid: selectedSeason!,
				searchParams: Object.fromEntries(searchParams?.entries() || [])
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!selectedSeason && !disabled
	});
};

export const useJoinRequest = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ApproveOrDeclinePlayerRequest) => {
			try {
				const apiResponse = await playerJoinRequest({
					organizationUuid: data.organizationId,
					teamLeaguesUuid: data.teamLeagueId,
					teamUuid: data.teamId,
					teamLeagueTradeRequestUuid: data.tradeRequestUuid,
					action: data.action,
					seasonId: data.seasonId
				});

				if (!apiResponse?.data?.success) {
					throw new Error(`The request was not approved or declined`);
				}
			} catch (error) {
				throw new Error(`The request was not approved or declined`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [TEAM_PLAYER_ACTIVITY_QUERY_KEY] });
			toast.success('Completed successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useUpdateIsFreeAgentMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateIsFreeAgentRequest) => {
			try {
				const apiResponse = await updateIsFreeAgent(data);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			toast.success('Free agent status successfully updated.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useSetPlayerAsInjuredMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: SetPlayerAsInjuredRequest) => {
			try {
				const apiResponse = await setPlayerAsInjured(data);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: ['TEAM_LEAGUE_TEAM_PLAYERS'] });
			toast.success('Player successfully added to the injured reserve.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useUpdateInvitationCodeMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateInvitationCodeRequest) => {
			try {
				const apiResponse = await updateInvitationCode(data);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			toast.success('Invitation code has been updated successfully.');
		},
		onError: () => {
			toast.error('An error has occurred. Please try again later.');
		}
	});
};

export const useCreateInvitationCodeMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: CreateInvitationCodeRequest) => {
			try {
				const apiResponse = await createInvitationCode(data);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: (_data) => {
			queryClient.invalidateQueries({ queryKey: [ACTIVE_TEAMS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [PLAYER_ACTIVITY_QUERY_KEY] });
			toast.success('Invitation code has been created successfully.');
		},
		onError: () => {
			toast.error('The invitation code is no longer valid. Please generate a new one and set an expiration date.');
		}
	});
};

export const useSendMessageMutate = () => {
	return useMutation({
		mutationFn: async (data: SendMessageRequest) => {
			try {
				const apiResponse = await sendMessage(data);

				if (apiResponse?.statusCode >= 400) {
					throw new Error(`HTTP error ${apiResponse.statusCode}`);
				}
			} catch (error) {
				throw new Error(`HTTP error ${error}`);
			}
		},
		onSuccess: () => {
			toast.success('The message was sent successfully.');
		},
		onError: () => {
			toast.error('There was an error sending the message. Please try again later.');
		}
	});
};

export const useFeaturedLeaguesQuery = () => {
	return useQuery({
		queryKey: [FEATURED_LEAGUES_QUERY_KEY],
		queryFn: () => getFeaturedLeagues({ moduleFor: TEAM_LEAGUE_MODULE_FOR })
	});
};

export const useMLPStripQuery = () => {
	return useQuery({
		queryKey: [MLP_STRIP_QUERY_KEY],
		queryFn: () => getMLPStripEvents()
	});
};

export const useScoreFormatsQuery = () => {
	return useQuery({
		queryKey: [SCORE_FORMATS_QUERY_KEY],
		queryFn: () => getScoreFormats()
	});
};

export const usePlayerGraphStatsQuery = (playerUuid: string, searchParams: SearchParams) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PLAYER_GRAPH_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, searchParams?.season],
		queryFn: () =>
			getPlayerGraphStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				searchParams: searchParams
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const usePlayerOverallStatsQuery = (playerUuid: string, searchParams: SearchParams) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PLAYER_OVERALL_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, searchParams?.season],
		queryFn: () =>
			getPlayerOverallStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				searchParams: searchParams
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const usePartnersOpponentsStatsQuery = (playerUuid: string, formatType: string, playerType: string) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [PARTNERS_OPPONENTS_STATS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, playerUuid, formatType, playerType],
		queryFn: () =>
			getPartnersOpponentsStats({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				playerUuid: playerUuid,
				formatType: formatType,
				playerType: playerType
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!playerUuid
	});
};

export const useScheduledEventsQuery = (
	seasonUuid: string | undefined,
	searchParams: SearchParams,
	allowFilters: boolean = true,
	shouldCallApi: boolean = true,
	divisionUuid?: string
) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [
			SCHEDULED_EVENTS_QUERY_KEY,
			store.teamLeagueUuid,
			store.organizationUuid,
			allowFilters ? searchParams?.search : '',
			allowFilters ? searchParams?.startDate : '',
			allowFilters ? searchParams?.endDate : '',
			allowFilters ? searchParams?.district : '',
			allowFilters ? searchParams?.division : ''
		],
		queryFn: () =>
			getScheduledEvents({
				teamLeagueUuid: store.teamLeagueUuid,
				organizationUuid: store.organizationUuid,
				seasonUuid: seasonUuid,
				searchParams: searchParams,
				allowFilters: allowFilters,
				divisionUuid: divisionUuid
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!seasonUuid && shouldCallApi
	});
};

export const useDistrictsInfoQuery = (teamLeagueUuid: string, searchParams?: SearchParams) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [DISTRICTS_INFO_QUERY_KEY, teamLeagueUuid, store.organizationUuid, searchParams],
		queryFn: () => getDistricts({ teamLeagueUuid, organizationUuid: store.organizationUuid, searchParams }),
		enabled: !!(store.organizationUuid && teamLeagueUuid)
	});
};

export const useSystemUsersSearchQuery = (debouncedSearch: string, currentPage = 1, gender?: string) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [SYSTEM_USERS_SEARCH_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid, debouncedSearch, currentPage, gender],
		queryFn: () =>
			getSystemUsersSearch({
				searchParams: {
					search: debouncedSearch,
					page_size: DEFAULT_PAGE_SIZE.toString(),
					current_page: currentPage.toString(),
					...(gender ? { gender } : {})
				}
			}),
		enabled: !!debouncedSearch
	});
};

export const useSeasonDivisionSubstitutePlayersQuery = (matchupGroupUuid: string | null | undefined) => {
	const store = useOrganizationStore();
	return useQuery({
		queryKey: [SEASON_DIVISION_SUBSTITUTE_PLAYERS_QUERY_KEY, store.teamLeagueUuid, store.organizationUuid],
		queryFn: () =>
			getSeasonDivisionSubstitutePlayers({
				organizationUuid: store.organizationUuid,
				teamLeagueUuid: store.teamLeagueUuid,
				matchupGroupUuid: matchupGroupUuid ?? ''
			}),
		enabled: !!matchupGroupUuid
	});
};

interface ActiveTeamsArgs {
	selectedSeason: string | null;
	disabled?: boolean;
	searchParams?: ReadonlyURLSearchParams;
}

export const useActiveTeams = ({ selectedSeason, disabled = false, searchParams }: ActiveTeamsArgs) => {
	const store = useOrganizationStore();

	return useQuery({
		queryKey: [ACTIVE_TEAMS_QUERY_KEY, store.organizationUuid, store.teamLeagueUuid, selectedSeason, searchParams?.get('district')],
		queryFn: () =>
			listActiveTeams({
				organizationUuid: store.organizationUuid,
				teamLeagueUuid: store.teamLeagueUuid,
				seasonUuid: selectedSeason!,
				searchParams: Object.fromEntries(searchParams?.entries() || [])
			}),
		enabled: !!store.organizationUuid && !!store.teamLeagueUuid && !!selectedSeason && !disabled
	});
};

interface UseFavoriteToggleParams {
	moduleUuid: string;
	moduleFor: number;
	initialIsFavorite?: boolean;
	onRefetch?: () => void;
}

interface UseFavoriteToggleResult {
	isFavorited: boolean;
	isDisabled: boolean;
	handleFavoriteToggle: (event: React.MouseEvent) => Promise<void>;
}

export function useFavoriteToggle({ moduleUuid, initialIsFavorite = false, onRefetch }: UseFavoriteToggleParams): UseFavoriteToggleResult {
	const [isFavorited, setIsFavorited] = useState(initialIsFavorite);
	const [isDisabled, setIsDisabled] = useState(false);

	const handleFavoriteToggle = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			if (isDisabled) return;
			setIsDisabled(true);

			const newFavoritedState = !isFavorited;
			setIsFavorited(newFavoritedState);

			try {
				if (newFavoritedState) {
					await addLeagueToFavorite({ moduleUuid });
				} else {
					await removeLeagueFromFavorite({ moduleUuid });
				}
				onRefetch?.();
			} catch {
				setIsFavorited(!newFavoritedState);
			} finally {
				setIsDisabled(false);
			}
		},
		[isDisabled, isFavorited, moduleUuid, onRefetch]
	);

	return { isFavorited, isDisabled, handleFavoriteToggle };
}

export function useTeamLeagueFavorite(id: string, initialIsFavorite?: boolean, onRefetch?: () => void) {
	const { session } = useSession();

	const { isFavorited, isDisabled, handleFavoriteToggle } = useFavoriteToggle({
		moduleUuid: id,
		moduleFor: TEAM_LEAGUES_MODULE,
		initialIsFavorite,
		onRefetch
	});

	return {
		isFavorite: isFavorited,
		isFavoriteDisabled: isDisabled,
		isUserLoggedIn: !!session?.user?.uuid,
		onToggle: (event: React.MouseEvent) => handleFavoriteToggle(event)
	};
}
